import { isFeatureFlagEnabled } from '@/shared/featureFlags/appearanceFeatureFlags';
import {
  BuildsIcon,
  Code,
  DescriptionOutlinedIcon,
  DriveFileMoveIcon,
  FeaturesIcon,
  FolderOutlinedIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ProjectsIcon,
  ReleasesIcon,
  SettingsIcon,
  StagesIcon,
  SuitesIcon,
  TestsIcon,
  type SearchIcon,
  UploadFileIcon,
} from '@/ui';
import type { GlobalSearchResult } from '@/features/search/data';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';

type NavigationResultDefinition = {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  aliases: string[];
  icon?: typeof SearchIcon;
  featureFlag?: string;
  superuserOnly?: boolean;
};

const navigationResults: NavigationResultDefinition[] = [
  {
    id: 'execution-runs',
    title: 'Execution Runs',
    subtitle: 'Page',
    href: '/execution-runs',
    aliases: ['execution', 'executions', 'runs', 'run history', 'test runs'],
    icon: PlayCircleFilledWhiteOutlinedIcon,
  },
  {
    id: 'uploads',
    title: 'Uploads',
    subtitle: 'Page',
    href: '/uploads',
    aliases: ['upload', 'uploads', 'results files', 'files', 'import'],
    icon: UploadFileIcon,
  },
  {
    id: 'releases',
    title: 'Releases',
    subtitle: 'Page',
    href: '/releases',
    aliases: ['release', 'releases'],
    icon: ReleasesIcon,
    featureFlag: 'releases',
  },
  {
    id: 'features',
    title: 'Features',
    subtitle: 'Page',
    href: '/features',
    aliases: ['feature', 'features'],
    icon: FeaturesIcon,
    featureFlag: 'features',
  },
  {
    id: 'suites',
    title: 'Suites',
    subtitle: 'Page',
    href: '/suites',
    aliases: ['suite', 'suites'],
    icon: SuitesIcon,
  },
  {
    id: 'tests',
    title: 'Tests',
    subtitle: 'Page',
    href: '/tests',
    aliases: ['test', 'tests'],
    icon: TestsIcon,
  },
  {
    id: 'pipelines',
    title: 'Pipelines',
    subtitle: 'Page',
    href: '/pipelines',
    aliases: ['pipeline', 'pipelines', 'ci'],
    icon: BuildsIcon,
    featureFlag: 'pipelines',
  },
  {
    id: 'stages',
    title: 'Stages',
    subtitle: 'Page',
    href: '/stages',
    aliases: ['stage', 'stages'],
    icon: DriveFileMoveIcon,
    featureFlag: 'pipelines',
  },
  {
    id: 'jobs',
    title: 'Jobs',
    subtitle: 'Page',
    href: '/jobs',
    aliases: ['job', 'jobs'],
    icon: StagesIcon,
    featureFlag: 'pipelines',
  },
  {
    id: 'coverage-runs',
    title: 'Coverage Runs',
    subtitle: 'Page',
    href: '/coverage-runs',
    aliases: ['coverage', 'coverage runs', 'code coverage'],
    icon: Code,
  },
  {
    id: 'coverage-directories',
    title: 'Directories',
    subtitle: 'Page',
    href: '/coverage-directories',
    aliases: ['coverage directories', 'directories', 'folders'],
    icon: FolderOutlinedIcon,
  },
  {
    id: 'coverage-files',
    title: 'Files',
    subtitle: 'Page',
    href: '/coverage-files',
    aliases: ['coverage files', 'files'],
    icon: DescriptionOutlinedIcon,
  },
  {
    id: 'account-settings',
    title: 'Account Settings',
    subtitle: 'Settings',
    href: '/account-settings',
    aliases: ['account', 'profile', 'settings'],
  },
  {
    id: 'workspace-settings',
    title: 'Workspace Settings',
    subtitle: 'Settings',
    href: '/workspace-settings',
    aliases: ['workspace', 'organisation', 'organization', 'team', 'users', 'settings'],
  },
  {
    id: 'project-settings',
    title: 'Project Settings',
    subtitle: 'Settings',
    href: '/project-settings',
    aliases: ['project', 'settings'],
  },
  {
    id: 'plan-settings',
    title: 'Plan Settings',
    subtitle: 'Settings',
    href: '/plan-settings',
    aliases: ['plan', 'billing', 'subscription', 'settings'],
  },
  {
    id: 'superuser',
    title: 'Superuser',
    subtitle: 'Admin',
    href: '/superuser',
    aliases: ['admin', 'superuser'],
    superuserOnly: true,
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    subtitle: 'Admin',
    href: '/superuser?tab=feature-flags',
    aliases: ['flags', 'feature flags', 'admin'],
    superuserOnly: true,
  },
];

export const globalSearchResultTypeLabels: Readonly<Record<GlobalSearchResult['type'], string>> = {
  coverageDirectory: 'Coverage directory',
  coverageFile: 'Coverage file',
  coverageRun: 'Coverage run',
  executionRun: 'Execution run',
  feature: 'Feature',
  job: 'Job',
  navigation: 'Page',
  pipeline: 'Pipeline',
  project: 'Project',
  release: 'Release',
  resultsFile: 'Results file',
  stage: 'Stage',
  suite: 'Suite',
  test: 'Test',
};

const resultTypeIcons: Readonly<Record<GlobalSearchResult['type'], typeof SearchIcon>> = {
  coverageDirectory: FolderOutlinedIcon,
  coverageFile: DescriptionOutlinedIcon,
  coverageRun: Code,
  executionRun: PlayCircleFilledWhiteOutlinedIcon,
  feature: FeaturesIcon,
  job: StagesIcon,
  navigation: SettingsIcon,
  pipeline: BuildsIcon,
  project: ProjectsIcon,
  release: ReleasesIcon,
  resultsFile: UploadFileIcon,
  stage: DriveFileMoveIcon,
  suite: SuitesIcon,
  test: TestsIcon,
};

const navigationResultIcons = new Map<string, typeof SearchIcon>(
  navigationResults.flatMap((definition) =>
    definition.icon ? [[`navigation:${definition.id}`, definition.icon] as const] : [],
  ),
);

const buildNavigationResult = (definition: NavigationResultDefinition): GlobalSearchResult => ({
  id: `navigation:${definition.id}`,
  source: 'navigation',
  type: 'navigation',
  title: definition.title,
  subtitle: definition.subtitle,
  href: definition.href,
  context: { organisationId: null, projectId: null },
});

export const getGlobalSearchResultIcon = (result: GlobalSearchResult): typeof SearchIcon => {
  if (result.source !== 'navigation') return resultTypeIcons[result.type];
  return navigationResultIcons.get(result.id) ?? resultTypeIcons.navigation;
};

export const matchNavigationResults = (
  query: string,
  isSuperuser: boolean,
  featureFlags?: FeatureFlagResolveResult['flags'] | null,
): GlobalSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) {
    return [];
  }
  return navigationResults
    .filter((definition) => !definition.superuserOnly || isSuperuser)
    .filter(
      (definition) =>
        definition.featureFlag === undefined ||
        isFeatureFlagEnabled(featureFlags, definition.featureFlag),
    )
    .filter((definition) => {
      const values = [definition.title, definition.subtitle, ...definition.aliases];
      return values.some((value) => value.toLowerCase().includes(normalizedQuery));
    })
    .map(buildNavigationResult);
};
